// Tạo công việc mới (hoặc việc con của một việc có sẵn). Chọn mẫu quy trình để điền sẵn checklist, chu kỳ lặp, yêu cầu ảnh.
import React, { useEffect } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Modal, Select, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Plus, X } from 'lucide-react';
import { DEPARTMENTS, ME_ID, MEMBERS, PROJECTS, REPEAT_LABEL, projectOf, type Repeat, type WorkTask } from './data';
import useWorkStore from './store';
import useProjectStore from '../Project/store';
import { PROJECT_STATUS, isProjectClosed } from '../Project/data';
import { TEMPLATES, TEMPLATE_GROUPS, templateOf } from './templates';

type Values = {
  templateId?: string;
  name: string;
  description?: string;
  projectId: string;
  assigneeId?: string;
  followerIds: string[];
  range?: [Dayjs | null, Dayjs | null];
  important: boolean;
  urgent: boolean;
  needReview: boolean;
  needProof: boolean;
  repeat?: Repeat;
  checklist: string[];
};

export const projectOptions = DEPARTMENTS.map(d => ({
  label: d.name,
  options: PROJECTS.filter(p => p.departmentId === d.id).map(p => ({ value: p.id, label: p.name })),
}));

export const memberOptions = (departmentId?: string) => {
  const sorted = [...MEMBERS].sort((a, b) => Number(b.departmentId === departmentId) - Number(a.departmentId === departmentId));
  return sorted.map(m => ({ value: m.id, label: m.id === ME_ID ? `${m.name} (tôi)` : m.name, dept: DEPARTMENTS.find(d => d.id === m.departmentId)?.name }));
};

export const repeatOptions = [{ value: '', label: 'Không lặp' }, ...(Object.keys(REPEAT_LABEL) as Repeat[]).map(r => ({ value: r, label: REPEAT_LABEL[r] }))];

const templateOptions = TEMPLATE_GROUPS.map(g => ({
  label: g,
  options: TEMPLATES.filter(t => t.group === g).map(t => ({ value: t.id, label: t.name })),
}));

export default function TaskForm({ open, onClose, projectId, parent, onCreated }: {
  open: boolean; onClose: () => void; projectId?: string; parent?: WorkTask; onCreated?: (t: WorkTask) => void;
}) {
  const [form] = Form.useForm<Values>();
  const createTask = useWorkStore(s => s.createTask);
  const pid = Form.useWatch('projectId', form);
  const dept = projectOf(pid)?.departmentId;
  const info = useProjectStore(s => s.projects.find(p => p.id === pid));

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      projectId: parent?.projectId ?? projectId,
      followerIds: [],
      range: [dayjs(), parent?.dueAt ? dayjs(parent.dueAt) : null],
      important: false,
      urgent: false,
      needReview: true,
      needProof: false,
      checklist: [],
    });
  }, [open, parent, projectId, form]);

  // Chọn mẫu: điền sẵn nội dung, phòng ban, hạn chót, checklist — vẫn sửa được trước khi tạo
  const applyTemplate = (id?: string) => {
    const tpl = templateOf(id);
    if (!tpl) return;
    form.setFieldsValue({
      name: tpl.name,
      description: tpl.description,
      projectId: parent?.projectId ?? `t-${tpl.departmentId}`,
      range: [dayjs(), dayjs().add(tpl.dueDays, 'day')],
      important: !!tpl.important,
      urgent: !!tpl.urgent,
      needReview: tpl.needReview,
      needProof: !!tpl.needProof,
      repeat: parent ? undefined : tpl.repeat,
      checklist: tpl.checklist,
    });
  };

  const submit = async () => {
    const v = await form.validateFields();
    const task = createTask({
      name: v.name.trim(),
      description: v.description?.trim(),
      projectId: v.projectId,
      parentId: parent?.id,
      assigneeId: v.assigneeId,
      followerIds: v.followerIds ?? [],
      startAt: v.range?.[0]?.format('YYYY-MM-DD'),
      dueAt: v.range?.[1]?.format('YYYY-MM-DD'),
      important: v.important,
      urgent: v.urgent,
      needReview: v.needReview,
      needProof: v.needProof || undefined,
      repeat: v.repeat || undefined,
      checklist: v.checklist,
      templateId: v.templateId,
    }, ME_ID);
    message.success(v.assigneeId ? `Đã tạo ${task.code} và giao việc` : `Đã tạo ${task.code} — chưa giao người làm`);
    onCreated?.(task);
    onClose();
  };

  return (
    <Modal open={open} zIndex={1100} onCancel={onClose} onOk={submit} okText="Tạo công việc" cancelText="Huỷ" width={680} destroyOnHidden
      title={parent ? <span>Thêm việc con · <span className="font-normal text-slate-500">{parent.code} {parent.name}</span></span> : 'Tạo công việc'}>
      <Form form={form} layout="vertical" className="mt-3">
        <Form.Item name="templateId" label="Mẫu quy trình" extra="Không bắt buộc — chọn để điền sẵn checklist, chu kỳ lặp và yêu cầu ảnh theo quy trình chuẩn">
          <Select allowClear showSearch optionFilterProp="label" placeholder="VD: Kiểm tra hộp thuốc cấp cứu phản vệ" options={templateOptions} onChange={applyTemplate}
            optionRender={o => {
              const repeat = templateOf(String(o.value))?.repeat;
              return <span>{o.label}{repeat && <span className="text-xs text-slate-400"> · {REPEAT_LABEL[repeat]}</span>}</span>;
            }} />
        </Form.Item>
        <Form.Item name="name" label="Tên công việc" rules={[{ required: true, whitespace: true, message: 'Nhập tên công việc' }]}>
          <Input autoFocus maxLength={200} placeholder="VD: Chuẩn bị phòng thủ thuật cho ca 14h" />
        </Form.Item>
        <Form.Item name="description" label="Mô tả / yêu cầu kết quả">
          <Input.TextArea rows={3} maxLength={2000} placeholder="Cần làm gì, kết quả bàn giao là gì…" />
        </Form.Item>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item name="projectId" label="Dự án / phòng ban" rules={[{ required: true, message: 'Chọn dự án hoặc phòng ban' }, { validator: () => (info && isProjectClosed(info) ? Promise.reject(new Error(`Dự án đã ${PROJECT_STATUS[info.status].label.toLowerCase()} — mở lại dự án để thêm việc`)) : Promise.resolve()) }]}>
            <Select showSearch optionFilterProp="label" options={projectOptions} disabled={!!parent} placeholder="Chọn" />
          </Form.Item>
          <Form.Item name="assigneeId" label="Người làm" extra="Để trống nếu chưa giao — việc sẽ nằm ở mục Chưa giao">
            <Select allowClear showSearch optionFilterProp="label" placeholder="Chọn người làm" options={memberOptions(dept)}
              optionRender={o => <span>{o.label} <span className="text-xs text-slate-400">· {(o.data as { dept?: string }).dept}</span></span>} />
          </Form.Item>
          <Form.Item name="range" label="Bắt đầu → Hạn chót"
            extra={info?.taskWithinProject && info.startAt && info.endAt ? `Trong thời gian dự án: ${dayjs(info.startAt).format('DD/MM')} – ${dayjs(info.endAt).format('DD/MM/YYYY')}` : undefined}
            rules={[{
              validator: (_, v?: [Dayjs | null, Dayjs | null]) => {
                if (v?.[0] && v?.[1] && v[1].isBefore(v[0], 'day')) return Promise.reject(new Error('Hạn chót phải sau ngày bắt đầu'));
                if (info?.taskWithinProject && ((info.startAt && v?.[0]?.isBefore(info.startAt, 'day')) || (info.endAt && v?.[1]?.isAfter(info.endAt, 'day'))))
                  return Promise.reject(new Error('Thời gian công việc phải nằm trong thời gian dự án'));
                return Promise.resolve();
              },
            }]}>
            <DatePicker.RangePicker className="w-full" format="DD/MM/YYYY" allowEmpty={[true, true]} placeholder={['Bắt đầu', 'Hạn chót']} />
          </Form.Item>
          <Form.Item name="followerIds" label="Người theo dõi">
            <Select mode="multiple" allowClear optionFilterProp="label" placeholder="Nhận thông báo, không phải làm" options={memberOptions(dept)} maxTagCount="responsive" />
          </Form.Item>
          {!parent && (
            <Form.Item name="repeat" label="Lặp lại" extra="Đóng lần này, hệ thống tự tạo lần kế tiếp cho cùng người làm"
              getValueProps={v => ({ value: v ?? '' })} normalize={v => v || undefined}>
              <Select options={repeatOptions} />
            </Form.Item>
          )}
        </div>

        <Form.Item label="Checklist quy trình" extra="Người làm phải tích đủ các mục mới báo hoàn thành được" className="!mb-3">
          <Form.List name="checklist">
            {(fields, { add, remove }) => (
              <div className="space-y-1.5">
                {fields.map((f, i) => (
                  <div key={f.key} className="flex items-center gap-2">
                    <span className="w-5 shrink-0 text-right text-xs text-slate-400">{i + 1}.</span>
                    <Form.Item name={f.name} noStyle><Input maxLength={200} placeholder="Nội dung cần kiểm tra / thực hiện" /></Form.Item>
                    <Button type="text" size="small" icon={<X size={14} />} onClick={() => remove(f.name)} aria-label="Xoá mục" />
                  </div>
                ))}
                <Button type="dashed" size="small" icon={<Plus size={14} />} onClick={() => add('')}>Thêm mục</Button>
              </div>
            )}
          </Form.List>
        </Form.Item>

        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <Form.Item name="important" valuePropName="checked" noStyle><Checkbox>Quan trọng</Checkbox></Form.Item>
          <Form.Item name="urgent" valuePropName="checked" noStyle><Checkbox>Khẩn cấp</Checkbox></Form.Item>
          <Form.Item name="needReview" valuePropName="checked" noStyle><Checkbox>Người giao duyệt kết quả trước khi hoàn thành</Checkbox></Form.Item>
          <Form.Item name="needProof" valuePropName="checked" noStyle><Checkbox>Báo xong phải kèm ảnh kết quả</Checkbox></Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
